import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import GradeProgressionBar, { STATUS_PILL, STATUS_LABEL } from '../components/GradeProgressionBar';
import GradingExportButton from '../components/GradingExportButton';
import { useAuth } from '../contexts/AuthContext';
import { apiGet, apiPut, apiPost, apiPatch, apiFetch } from '../utils/api';
import useUnsavedChangesGuard from '../hooks/useUnsavedChangesGuard';
import { computeGradingProgress } from '../utils/gradingStats';
import {
  DISCIPLINE_LABELS,
  GRADE_ORDER,
  GRADE_LABELS,
  QUALIFICATIONS,
} from '../constants/grading';
import { BTN } from '../constants/buttons';
import { FIELD_LABEL_CLASS } from '../constants/inputs';
import { MODAL_OVERLAY, MODAL_PANEL_MOBILE_CAP } from '../constants/overlayLayers';
import NumberInput, { numberEntryProblem } from '../components/ui/NumberInput';
import { toPayloadNumber } from '../utils/moneyPayload';

// Remembered-field localStorage keys. Reuse the EXISTING shared identity keys
// (set by AuthContext + NewOrder) rather than inventing grading-specific ones,
// so the applicant's name/email/phone stay consistent across the app.
const LS_KEYS = {
  fullName: 'sfx_requested_by',
  email: 'sfx_buyer_email',
  phone: 'sfx_user_phone',
};

// Shared input styling (mirrors NewOrder's input idiom, with dark-mode variants).
const inputClass =
  'w-full px-3 h-11 md:h-[38px] border border-gray-300 dark:border-gray-600 rounded-lg ' +
  'text-base bg-slate-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 ' +
  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
const textareaClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg ' +
  'text-base bg-slate-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 ' +
  'focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
// 🎨 Owner ruling 2026-09-28: every field caption matches the order form's
// item-label style — see `constants/inputs.js#FIELD_LABEL_CLASS`.
const labelClass = FIELD_LABEL_CLASS;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// True when a value is present but is NOT a valid email (empty = not invalid).
const isInvalidEmail = (v) => {
  const s = (v == null ? '' : String(v)).trim();
  return s !== '' && !EMAIL_RE.test(s);
};

// Stable client-side key for list rows (entries + signatures) so React keying
// survives mid-list removal. Server rows are keyed by their DB id; only fresh
// rows need this synthetic key.
function makeClientKey() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // ignore — fall through to the timestamp+random fallback
  }
  return String(Date.now()) + '-' + Math.random().toString(36).slice(2);
}

// Section wrapper — mirrors OrderForm's section-title pattern (bg-white card
// with shadow, edge-to-edge header strip via negative margins, compact px-2 py-1
// header that hover-greys, h3 text-lg title). Defined locally so the Grading
// page's visual frame matches /orders/:id/edit exactly without bolting another
// variant onto the shared CollapsibleCard component (which keeps its tinted/
// numbered idiom for RA Detail's 5 numbered sections).
function GradingSection({ id, title, defaultCollapsed = false, children }) {
  const lsKey = 'sfx_grading_section_' + id;
  const [collapsed, setCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem(lsKey);
      if (saved !== null) return saved === 'true';
    } catch { /* ignore */ }
    return defaultCollapsed;
  });

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(lsKey, String(next)); } catch { /* ignore */ }
      return next;
    });
  };

  return (
    <div
      className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-2.5 mt-4 space-y-4"
      data-testid={`grading-section-${id}`}
      data-collapsed={collapsed ? 'true' : 'false'}
    >
      <button
        type="button"
        onClick={toggle}
        aria-expanded={!collapsed}
        className="flex items-center justify-between w-full text-left -mx-2.5 -mt-2.5 px-2 py-1 border-b border-gray-200 dark:border-gray-700 rounded-t-lg hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
        data-testid={`grading-section-toggle-${id}`}
        data-section-title
      >
        <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-200">{title}</h3>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-4 w-4 text-gray-400 transition-transform ${collapsed ? '-rotate-90' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {!collapsed && children}
    </div>
  );
}

// Server-enforced cap on attachments per profile. Mirrored client-side so the
// drop-zone hides at the limit instead of letting the user hit a 413.
const MAX_ATTACHMENTS = 20;

// iPhone defaults to HEIC; pdf-lib can only embed JPEG + PNG. Convert
// transparently before upload. Lazy-loads ~200KB only when needed.
async function normaliseFile(file) {
  const isHeic =
    /^image\/heic|^image\/heif/i.test(file.type) ||
    /\.(heic|heif)$/i.test(file.name);
  if (!isHeic) return file;
  const { default: heic2any } = await import('heic2any');
  const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.92 });
  const blob = Array.isArray(out) ? out[0] : out;
  const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
  return new File([blob], newName, { type: 'image/jpeg' });
}

function humanFileSize(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Drop-zone for the Supporting documents section — click or drag-drop.
// Mirrors the OrderForm dashed-border pattern.
function FileDropArea({ accept, onFiles, disabled }) {
  const inputRef = React.useRef(null);
  const [dragOver, setDragOver] = React.useState(false);
  if (disabled) {
    return (
      <div className="border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg p-3 text-center bg-gray-50 dark:bg-gray-700/30">
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Attachment limit reached ({MAX_ATTACHMENTS}). Remove one to add another.
        </p>
      </div>
    );
  }
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const list = Array.from(e.dataTransfer?.files || []);
    if (list.length > 0) onFiles(list);
  };
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => inputRef.current && inputRef.current.click()}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current?.click(); } }}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={onDrop}
      className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
        dragOver
          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
          : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
      }`}
      data-testid="grading-attachments-drop"
    >
      <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
        {dragOver ? 'Drop files here' : 'Click or drag files to attach'}
      </p>
      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
        JPEG, PNG, HEIC, or PDF · max 20MB each · these are appended to your application PDF
      </p>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={accept}
        className="hidden"
        onChange={(e) => {
          const list = Array.from(e.target.files || []);
          if (list.length > 0) onFiles(list);
          // Reset so picking the same file again still fires onChange.
          e.target.value = '';
        }}
        data-testid="grading-attachments-input"
      />
    </div>
  );
}

// One attachment row — thumbnail + editable label + size + actions.
function AttachmentRow({ attachment, onLabelChange, onDelete }) {
  const [label, setLabel] = React.useState(attachment.label || attachment.file_name);
  const dirty = label !== (attachment.label || attachment.file_name);
  const isImage = /^image\//.test(attachment.file_type);
  const commit = () => { if (dirty) onLabelChange(label); };
  return (
    <li className="flex items-center gap-3 p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30">
      <div className="flex-shrink-0 w-12 h-12 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 flex items-center justify-center overflow-hidden">
        {isImage ? (
          <img
            src={attachment.download_url}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); e.target.blur(); } }}
          className="w-full px-2 py-1 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
          aria-label="Attachment label"
          data-testid={`grading-attachment-label-${attachment.id}`}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {attachment.file_name} · {humanFileSize(attachment.file_size)}
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <a
          href={attachment.download_url}
          download={attachment.file_name}
          className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
          data-testid={`grading-attachment-download-${attachment.id}`}
        >
          Download
        </a>
        <button
          type="button"
          onClick={onDelete}
          className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium"
          data-testid={`grading-attachment-remove-${attachment.id}`}
        >
          Remove
        </button>
      </div>
    </li>
  );
}

// Row for a NEW file that's been queued client-side but not yet POSTed to
// the server. No download link (no server URL yet); Remove just drops it
// from the queue. A small amber "Queued — save to upload" tag makes the
// pending state obvious.
function StagedAttachmentRow({ staged, onLabelChange, onRemove }) {
  const [label, setLabel] = React.useState(staged.label || staged.file.name);
  const dirty = label !== (staged.label || staged.file.name);
  const isImage = /^image\//.test(staged.file.type);
  const previewUrl = React.useMemo(
    () => (isImage ? URL.createObjectURL(staged.file) : null),
    [staged.file, isImage]
  );
  React.useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);
  const commit = () => { if (dirty) onLabelChange(label); };
  return (
    <li className="flex items-center gap-3 p-2 rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-900/15">
      <div className="flex-shrink-0 w-12 h-12 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 flex items-center justify-center overflow-hidden">
        {previewUrl ? (
          <img src={previewUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
          </svg>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commit(); e.target.blur(); } }}
          className="w-full px-2 py-1 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-blue-500"
          aria-label="Attachment label"
          data-testid={`grading-staged-label-${staged._key}`}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
          {staged.file.name} · {humanFileSize(staged.file.size)} ·{' '}
          <span className="text-amber-700 dark:text-amber-400 font-semibold">Queued — save to upload</span>
        </p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={onRemove}
          className="text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium"
          data-testid={`grading-staged-remove-${staged._key}`}
        >
          Remove
        </button>
      </div>
    </li>
  );
}

// Passport-style photo widget — sits in the 3rd column of Applicant Details.
// Portrait 4:5 placeholder when empty; image preview with Replace + Remove
// overlay buttons once uploaded. Single-file picker.
function PhotoUpload({ photo, onUpload, onRemove, uploading }) {
  const inputRef = React.useRef(null);
  const openPicker = () => inputRef.current && inputRef.current.click();
  return (
    <div className="space-y-2">
      <label className={FIELD_LABEL_CLASS}>
        Passport-style photo
      </label>
      <div className="relative w-full max-w-[180px] aspect-[4/5] rounded-lg overflow-hidden border-2 border-dashed border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/30">
        {photo ? (
          <>
            <img
              src={photo.download_url}
              alt="Passport"
              className="w-full h-full object-cover"
              data-testid="grading-photo-preview"
            />
            <div className="absolute inset-x-0 bottom-0 flex justify-between gap-1 p-1 bg-black/55">
              <button
                type="button"
                onClick={openPicker}
                disabled={uploading}
                className="flex-1 text-xs font-medium text-white hover:underline disabled:opacity-50"
                data-testid="grading-photo-replace"
              >
                {uploading ? 'Uploading…' : 'Replace'}
              </button>
              <button
                type="button"
                onClick={onRemove}
                disabled={uploading}
                className="flex-1 text-xs font-medium text-red-300 hover:text-red-200 hover:underline disabled:opacity-50"
                data-testid="grading-photo-remove"
              >
                Remove
              </button>
            </div>
          </>
        ) : (
          <button
            type="button"
            onClick={openPicker}
            disabled={uploading}
            className="w-full h-full flex flex-col items-center justify-center gap-2 p-3 text-center hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors disabled:opacity-60"
            data-testid="grading-photo-add"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
            </svg>
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
              {uploading ? 'Uploading…' : 'Add passport photo'}
            </span>
            <span className="text-[10px] text-gray-400 dark:text-gray-500 leading-tight">
              JPEG, PNG, or HEIC
            </span>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif"
        className="hidden"
        onChange={(e) => {
          const file = (e.target.files || [])[0];
          if (file) onUpload(file);
          e.target.value = '';
        }}
        data-testid="grading-photo-input"
      />
    </div>
  );
}

// Empty experience entry (mirrors NewOrder's createEmptyItem pattern).
function createEmptyEntry() {
  return {
    _key: makeClientKey(),
    production_name: '',
    supervisor_name: '',
    credited_grade: '',
    start_date: '',
    end_date: '',
    weeks_worked: 0,
    odd_days: 0,
    total_days: 0,
    effects_techniques: '',
    notes: '',
    reference_title: '',
    reference_email: '',
  };
}

// ── Canonical bundle for dirty-tracking ─────────────────────────────
// Build a STABLE JSON string from ONLY the editable fields, with deterministic
// key order and type/null normalization. This is the single source of truth for
// "is the form dirty?". It must produce identical output for values that differ
// only by dialect/echo artifacts:
//   - text: null and "" compare equal      → String(x ?? '')
//   - numbers: 12 and "12" compare equal    → Number(x) || 0
//   - empty numerics normalize to 0
// Server metadata (id/user_id/created_at/updated_at/profile_id/sort_order) and
// the synthetic _key are deliberately excluded — they must never drive dirtiness.
const CANONICAL_PROFILE_TEXT_FIELDS = [
  'full_name',
  'date_of_birth',
  'email',
  'phone',
  'address',
  'post_code',
  'discipline',
  'current_grade',
  'grade_applied_for',
  'subsidiary_title',
  'date_entered_industry',
  'date_last_grading',
  'other_qualifications',
  'other_experience',
];

const CANONICAL_ENTRY_TEXT_FIELDS = [
  'production_name',
  'supervisor_name',
  'credited_grade',
  'start_date',
  'end_date',
  'effects_techniques',
  'notes',
  'reference_title',
  'reference_email',
];

function canonStr(x) {
  return String(x ?? '');
}

// Blank → 0; a readable number → itself; text that is NOT a number stays its own text, so an
// unreadable box over a stored 0 still reads as an unsaved change (it used to collapse to 0).
function canonNum(x) {
  const n = toPayloadNumber(x, 0);
  return n === null ? String(x) : n;
}

/**
 * THE EXPERIENCE NUMBERS' RULES (input audit #5, 2026-10-28) — a CONVENIENCE MIRROR of
 * `server/routes/grading.js#GRADING_NUMBER_RULES`, which is the gate. `dp` mirrors its weeks
 * rule too (review N-4, 2026-10-29): left to the server, `2.555` reached the save bar as the raw
 * key wording; the server checks decimals BEFORE the maximum, and so does `entryNumberProblem`.
 * Blank pyro = NULL (not stated).
 */
const GRADING_NUMBER_FIELDS = [
  { key: 'weeks_worked', label: 'Weeks worked', integer: false, dp: 2, max: 5200 },
  { key: 'odd_days', label: 'Odd days', integer: true, max: 36500 },
];
const PYRO_NUMBER_FIELDS = [
  { key: 'pyro_years', label: 'Pyrotechnic years', integer: true, max: 100 },
  { key: 'pyro_months', label: 'Pyrotechnic months', integer: true, max: 1200 },
];
const GRADING_PROBLEM_WORDS = {
  malformed: 'is not a number',
  negative: 'cannot be negative',
  fraction: 'must be a whole number',
  too_large: 'is too large',
  too_many_decimals: 'must have at most 2 decimal places',
};
/** `numberEntryProblem`, plus the server's decimal-places rule for a field carrying `dp`. */
function entryNumberProblem(value, f) {
  const problem = numberEntryProblem(value, f);
  if (f.dp == null || (problem && problem !== 'too_large')) return problem;
  const n = toPayloadNumber(value, null);
  if (n !== null && Math.abs(n * 10 ** f.dp - Math.round(n * 10 ** f.dp)) > 1e-9) return 'too_many_decimals';
  return problem;
}
// A value equal to what was LOADED is never refused — the server's echo-hatch, mirrored, so a legacy
// stored value (or a pyro figure on a profile whose pyro boxes are hidden) cannot brick a save.
const sameAsSaved = (cur, saved) => saved !== null && saved !== undefined && String(saved) === String(cur);

/**
 * The first refused experience number as a sentence, or null. Locked entries are not this save's.
 * `saved` = the bundle as last loaded/saved (`{ profile, entries }`), for the echo-hatch.
 */
export function gradingNumbersError(profile, entries, saved) {
  const p = profile || {};
  const savedProfile = (saved && saved.profile) || {};
  const savedEntries = new Map(((saved && saved.entries) || []).filter((e) => e && e.id != null).map((e) => [e.id, e]));
  for (const f of PYRO_NUMBER_FIELDS) {
    if (sameAsSaved(p[f.key], savedProfile[f.key])) continue;
    const problem = numberEntryProblem(p[f.key], f);
    if (problem) return `${f.label} ${GRADING_PROBLEM_WORDS[problem]} (0 to ${f.max}).`;
  }
  const list = Array.isArray(entries) ? entries : [];
  for (let i = 0; i < list.length; i++) {
    const e = list[i] || {};
    if (isLocked(e)) continue;
    const before = e.id != null ? savedEntries.get(e.id) : undefined;
    for (const f of GRADING_NUMBER_FIELDS) {
      if (before && sameAsSaved(e[f.key], before[f.key])) continue;
      const problem = entryNumberProblem(e[f.key], f);
      const where = e.production_name && String(e.production_name).trim() ? `"${String(e.production_name).trim()}"` : `production ${i + 1}`;
      if (problem) return `${f.label} on ${where} ${GRADING_PROBLEM_WORDS[problem]} (0 to ${f.max}).`;
    }
  }
  return null;
}

// total_days is a locked, derived field: (5-day weeks × 5) + odd days.
// Single source of truth used by display, save, the dirty-compare and stats.
function entryTotalDays(e) {
  const weeks = Math.max(0, Number(e && e.weeks_worked) || 0);
  const odd = Math.max(0, Number(e && e.odd_days) || 0);
  return weeks * 5 + odd;
}

// An entry is locked (inputs frozen, cannot be removed) once its reference
// approval has been requested ('pending') or granted ('approved'). 'rejected'
// and 'none' stay fully editable. Server enforces the same rule.
const isLocked = (entry) =>
  entry && (entry.approval_status === 'pending' || entry.approval_status === 'approved');

// Shared locked-input visual idiom (matches the read-only total_days field).
const LOCKED_INPUT = ' bg-gray-100 dark:bg-slate-700 cursor-not-allowed';

function canonicalBundle(profile, entries) {
  const p = profile || {};
  const canonProfile = {};
  for (const f of CANONICAL_PROFILE_TEXT_FIELDS) {
    canonProfile[f] = canonStr(p[f]);
  }
  // Pyro numerics: normalize empty → 0 (applied identically on both sides).
  canonProfile.pyro_years = canonNum(p.pyro_years);
  canonProfile.pyro_months = canonNum(p.pyro_months);
  // qualifications + publish_permissions: plain objects (default {}).
  canonProfile.qualifications =
    p.qualifications && typeof p.qualifications === 'object' ? p.qualifications : {};
  canonProfile.publish_permissions =
    p.publish_permissions && typeof p.publish_permissions === 'object' ? p.publish_permissions : {};
  // supervisor_signatures: array of {name,grade,relationship} String-coerced.
  const sigs = Array.isArray(p.supervisor_signatures) ? p.supervisor_signatures : [];
  canonProfile.supervisor_signatures = sigs.map((s) => ({
    name: canonStr(s && s.name),
    grade: canonStr(s && s.grade),
    relationship: canonStr(s && s.relationship),
  }));

  const ents = Array.isArray(entries) ? entries : [];
  const canonEntries = ents.map((e) => {
    const row = {};
    for (const f of CANONICAL_ENTRY_TEXT_FIELDS) {
      row[f] = canonStr(e[f]);
    }
    row.weeks_worked = canonNum(e.weeks_worked);
    row.odd_days = canonNum(e.odd_days);
    row.total_days = entryTotalDays(e); // derived, not the stored value
    return row;
  });

  return JSON.stringify({ profile: canonProfile, entries: canonEntries });
}

// The contact-detail toggles for the GDPR publish-permissions section.
const PUBLISH_FIELDS = [
  { id: 'name', label: 'Name' },
  { id: 'address', label: 'Address' },
  { id: 'mobile', label: 'Mobile' },
  { id: 'email', label: 'Email' },
  { id: 'phone', label: 'Phone' },
];

// Reference signatory title options (References section "Title" dropdown).
const REFERENCE_TITLES = [
  'Supervisor',
  'Production Manager',
  '1st AD',
  'Producer',
  'Director',
];

function Grading() {
  const { user } = useAuth();

  // ── Data state ──────────────────────────────────────────────────
  const [profile, setProfile] = useState(null);
  const [entries, setEntries] = useState([]);
  const [pristine, setPristine] = useState(null); // canonical JSON string of last-saved bundle
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Keep a live ref to entries so effects that shouldn't depend on `entries`
  // (e.g. the one-shot prefill) can still read the current value.
  const entriesRef = useRef(entries);
  useEffect(() => {
    entriesRef.current = entries;
  }, [entries]);

  // ── Save state ──────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);
  const savingRef = useRef(false);
  const savedFlashTimer = useRef(null);
  // Raw (un-normalized) snapshot of the last-saved state, used by Cancel to
  // restore the actual field values. Dirty-tracking uses the canonical string;
  // revert uses this raw object so real values come back intact.
  const rawSnapshot = useRef(null);

  // ── Experience collapse state (Set of stable entry keys that are collapsed) ──
  const [collapsedEntries, setCollapsedEntries] = useState(() => new Set());
  // Supporting documents — `attachments` is the server-persisted list.
  // `stagedAttachments` holds NEW uploads queued client-side; they only POST
  // to the server when the user clicks Save changes. This keeps newly-added
  // files visible immediately, but defers persistence (and therefore the
  // download URL) until the canonical save round-trip — so Download never
  // races a freshly-written file.
  const [attachments, setAttachments] = useState([]);
  const [stagedAttachments, setStagedAttachments] = useState([]); // [{_key, file, label}]
  const [attachmentError, setAttachmentError] = useState('');
  const [uploadingAttachments, setUploadingAttachments] = useState(false);

  // ── Load on mount ───────────────────────────────────────────────
  const load = useCallback(() => {
    setLoading(true);
    setLoadError('');
    apiGet('/api/grading/me')
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load grading profile');
        return res.json();
      })
      .then((data) => {
        // Locked to a single discipline (Physical) for now — force it so any
        // legacy non-physical value can't drive the bar/eligibility.
        const prof = { ...(data.profile || {}), discipline: 'physical' };
        const ents = (Array.isArray(data.entries) ? data.entries : []).map((e) => ({
          ...e,
          _key: e._key || makeClientKey(),
        }));
        setProfile(prof);
        setEntries(ents);
        setAttachments(Array.isArray(data.attachments) ? data.attachments : []);
        setPristine(canonicalBundle(prof, ents));
        rawSnapshot.current = { profile: prof, entries: ents };
      })
      .catch((err) => setLoadError(err.message || 'Failed to load grading profile'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Fallback prefill: if the loaded profile has no name/email, seed from the
  // auth user (server prefills on create, but this covers older rows / races).
  // Never clobbers a value the user already has.
  useEffect(() => {
    if (!profile || !user) return;
    setProfile((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      let changed = false;
      if (!next.full_name && user.display_name) {
        next.full_name = user.display_name;
        changed = true;
      }
      if (!next.email && user.email) {
        next.email = user.email;
        changed = true;
      }
      if (changed) {
        // Auto-prefill is not a user edit — fold it into the pristine baseline
        // (and the raw revert snapshot) so it doesn't open the save bar on load
        // and Cancel restores the prefilled values.
        setPristine((cur) => (cur == null ? cur : canonicalBundle(next, entriesRef.current)));
        rawSnapshot.current = { profile: next, entries: entriesRef.current };
      }
      return changed ? next : prev;
    });
    // Only run once a profile is present and once the user is known.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile && profile.user_id, user && user.id]);

  // ── Derived: split attachments into photo (≤1) + documents ──────
  // Photo lives in its own Applicant Details slot; documents render in the
  // Supporting documents section. Photo is also a per-grade requirement.
  const photo = useMemo(
    () => attachments.find((a) => a.kind === 'photo') || null,
    [attachments]
  );
  const documentAttachments = useMemo(
    () => attachments.filter((a) => a.kind !== 'photo'),
    [attachments]
  );

  // ── Progress (headline visual) ──────────────────────────────────
  const progress = useMemo(
    () => computeGradingProgress(
      entries,
      profile ? profile.discipline : 'physical',
      profile || {},
      { hasPhoto: !!photo },
    ),
    [entries, profile, photo]
  );

  // ── Dirty tracking (JSON compare against pristine) ──────────────
  // Staged attachments also count as dirty — Save changes will POST them.
  const isDirty = useMemo(() => {
    if (stagedAttachments.length > 0) return true;
    if (pristine == null || profile == null) return false;
    return canonicalBundle(profile, entries) !== pristine;
  }, [profile, entries, pristine, stagedAttachments]);

  // Warn before leaving with unsaved changes — in-app navigation, Back, reload,
  // tab close (R14, 2026-09-15; was a bare `beforeunload` that could not see a
  // sidebar click). Staged attachments are part of `isDirty`.
  const leaveGuard = useUnsavedChangesGuard({
    when: isDirty,
    message: `Your grading changes haven't been saved. If you leave, they${stagedAttachments.length > 0 ? ' and any attached files' : ''} will be lost.`,
  });

  // Clear any pending savedFlash timer on unmount (avoid set-state-after-unmount).
  useEffect(() => () => {
    if (savedFlashTimer.current) clearTimeout(savedFlashTimer.current);
  }, []);

  // ── Profile field setters ───────────────────────────────────────
  const setProfileField = useCallback((field, value) => {
    setSavedFlash(false);
    setProfile((prev) => ({ ...(prev || {}), [field]: value }));
  }, []);

  // Setter that also persists to a remembered localStorage key.
  const setRememberedField = useCallback((field, value, lsKey) => {
    setProfileField(field, value);
    try {
      localStorage.setItem(lsKey, value || '');
    } catch {
      // ignore storage errors
    }
  }, [setProfileField]);

  const setQualification = useCallback((qualId, value) => {
    setSavedFlash(false);
    setProfile((prev) => {
      const quals = { ...(prev && prev.qualifications ? prev.qualifications : {}) };
      quals[qualId] = value;
      return { ...(prev || {}), qualifications: quals };
    });
  }, []);

  const setPublishPermission = useCallback((fieldId, value) => {
    setSavedFlash(false);
    setProfile((prev) => {
      const perms = { ...(prev && prev.publish_permissions ? prev.publish_permissions : {}) };
      perms[fieldId] = value;
      return { ...(prev || {}), publish_permissions: perms };
    });
  }, []);

  // ── Experience entries (NewOrder add/remove/collapse pattern) ────
  // Stable per-row key: DB id for server rows, synthetic _key for new rows.
  const entryKey = useCallback((entry) => (entry && entry.id != null ? `id:${entry.id}` : `k:${entry && entry._key}`), []);

  const toggleEntryCollapse = useCallback((key) => {
    setCollapsedEntries((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const updateEntry = useCallback((index, field, value) => {
    setSavedFlash(false);
    setEntries((prev) => {
      const updated = [...prev];
      const row = { ...updated[index], [field]: value };
      // total_days is locked = (5-day weeks × 5) + odd days. Recompute whenever
      // either input changes; the field itself is read-only in the UI.
      if (field === 'weeks_worked' || field === 'odd_days') {
        row.total_days = entryTotalDays(row);
      }
      updated[index] = row;
      return updated;
    });
  }, []);

  const addEntry = useCallback(() => {
    setSavedFlash(false);
    setEntries((prev) => [...prev, createEmptyEntry()]);
  }, []);

  const removeEntry = useCallback((index) => {
    setSavedFlash(false);
    let removedKey = null;
    setEntries((prev) => {
      const removed = prev[index];
      if (removed) removedKey = removed.id != null ? `id:${removed.id}` : `k:${removed._key}`;
      return prev.filter((_, i) => i !== index);
    });
    // Drop the removed row's stable key from the collapsed set (others unaffected).
    if (removedKey != null) {
      setCollapsedEntries((prev) => {
        if (!prev.has(removedKey)) return prev;
        const next = new Set(prev);
        next.delete(removedKey);
        return next;
      });
    }
  }, []);

  // ── Reference approval actions (request / withdraw) ─────────────
  // Guards double-submits across both actions. Per-entry error keyed by DB id.
  const approvalSubmittingRef = useRef(false);
  const [approvalError, setApprovalError] = useState({}); // { [entryId]: message }
  // Confirmation modal — null = closed, number = entry index pending confirmation.
  // Prevents accidental sends of half-finished paperwork to a referee.
  const [confirmRequestIndex, setConfirmRequestIndex] = useState(null);

  // Merge the server-returned approval_* fields into both live state and the
  // raw revert snapshot, matched by DB id. approval_* are NOT in the canonical
  // bundle, so this never changes `isDirty`; patching rawSnapshot ensures a
  // later Cancel keeps the new lock/status rather than reverting it.
  const patchEntryApproval = useCallback((entryId, serverEntry) => {
    if (entryId == null || !serverEntry) return;
    const fields = {
      approval_status: serverEntry.approval_status,
      approval_decided_at: serverEntry.approval_decided_at,
      approval_decided_by: serverEntry.approval_decided_by,
      approval_reject_reason: serverEntry.approval_reject_reason,
    };
    setEntries((prev) => prev.map((e) => (e.id === entryId ? { ...e, ...fields } : e)));
    const snap = rawSnapshot.current;
    if (snap && Array.isArray(snap.entries)) {
      snap.entries = snap.entries.map((e) => (e.id === entryId ? { ...e, ...fields } : e));
    }
  }, []);

  const setEntryApprovalError = useCallback((entryId, msg) => {
    setApprovalError((prev) => ({ ...prev, [entryId]: msg }));
  }, []);

  const requestApproval = useCallback(async (index) => {
    if (approvalSubmittingRef.current) return;
    const entry = entries[index];
    if (!entry || entry.id == null) return;
    setEntryApprovalError(entry.id, '');
    approvalSubmittingRef.current = true;
    try {
      const res = await apiPost(`/api/grading/entries/${entry.id}/request-approval`, {
        reference_email: entry.reference_email,
      });
      if (!res.ok) {
        let msg = 'Could not request approval';
        try {
          const data = await res.json();
          if (data && data.error) msg = data.error;
        } catch {
          // ignore parse error
        }
        throw new Error(msg);
      }
      const data = await res.json();
      patchEntryApproval(entry.id, data.entry || {});
    } catch (err) {
      setEntryApprovalError(entry.id, err.message || 'Could not request approval');
    } finally {
      approvalSubmittingRef.current = false;
    }
  }, [entries, patchEntryApproval, setEntryApprovalError]);

  const withdraw = useCallback(async (index) => {
    if (approvalSubmittingRef.current) return;
    const entry = entries[index];
    if (!entry || entry.id == null) return;
    setEntryApprovalError(entry.id, '');
    approvalSubmittingRef.current = true;
    try {
      const res = await apiPost(`/api/grading/entries/${entry.id}/withdraw`, {});
      if (!res.ok) {
        let msg = 'Could not unlock';
        try {
          const data = await res.json();
          if (data && data.error) msg = data.error;
        } catch {
          // ignore parse error
        }
        throw new Error(msg);
      }
      const data = await res.json();
      // Fall back to an explicit 'none' shape if the server returns a bare ok.
      patchEntryApproval(
        entry.id,
        data.entry || {
          approval_status: 'none',
          approval_decided_at: null,
          approval_decided_by: null,
          approval_reject_reason: null,
        }
      );
    } catch (err) {
      setEntryApprovalError(entry.id, err.message || 'Could not unlock');
    } finally {
      approvalSubmittingRef.current = false;
    }
  }, [entries, patchEntryApproval, setEntryApprovalError]);

  // ── Supporting documents (attachments) actions ──────────────────
  // Stage uploaded files locally — they don't hit the server until the user
  // clicks Save changes. Cap-check counts both server + staged so the user
  // can't queue 21 files even before saving.
  const uploadAttachments = useCallback(async (rawFiles) => {
    if (!rawFiles || rawFiles.length === 0) return;
    setAttachmentError('');
    setUploadingAttachments(true);
    try {
      const files = await Promise.all(rawFiles.map(normaliseFile));
      setStagedAttachments((prev) => {
        const existing = attachments.filter((a) => a.kind !== 'photo').length;
        const room = MAX_ATTACHMENTS - existing - prev.length;
        if (room <= 0) {
          setAttachmentError(`Attachment limit reached (${MAX_ATTACHMENTS}). Remove one before adding more.`);
          return prev;
        }
        const accepted = files.slice(0, room).map((f) => ({
          _key: makeClientKey(),
          file: f,
          label: f.name,
        }));
        if (accepted.length < files.length) {
          setAttachmentError(`Only ${accepted.length} file(s) added — attachment limit is ${MAX_ATTACHMENTS}.`);
        }
        return [...prev, ...accepted];
      });
    } catch (err) {
      setAttachmentError(err.message || 'Upload failed');
    } finally {
      setUploadingAttachments(false);
    }
  }, [attachments]);

  // Drop a staged (not-yet-saved) attachment.
  const removeStagedAttachment = useCallback((key) => {
    setStagedAttachments((prev) => prev.filter((a) => a._key !== key));
  }, []);

  // Edit the label on a staged attachment (in-memory only).
  const updateStagedLabel = useCallback((key, label) => {
    setStagedAttachments((prev) => prev.map((a) => (a._key === key ? { ...a, label } : a)));
  }, []);

  const updateAttachmentLabel = useCallback(async (fileId, label) => {
    setAttachmentError('');
    try {
      const res = await apiPatch(`/api/grading/me/attachments/${fileId}`, { label });
      if (!res.ok) throw new Error('Could not rename');
      const data = await res.json();
      setAttachments((prev) => prev.map((a) => (a.id === fileId ? data.attachment : a)));
    } catch (err) {
      setAttachmentError(err.message || 'Could not rename');
    }
  }, []);

  const deleteAttachment = useCallback(async (fileId) => {
    setAttachmentError('');
    try {
      const res = await apiFetch(`/api/grading/me/attachments/${fileId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Could not remove');
      setAttachments((prev) => prev.filter((a) => a.id !== fileId));
    } catch (err) {
      setAttachmentError(err.message || 'Could not remove');
    }
  }, []);

  // Photo-specific upload — uses the same endpoint with ?kind=photo, which
  // the server treats as a single-photo-per-profile slot (replaces on upload).
  const uploadPhoto = useCallback(async (rawFile) => {
    setAttachmentError('');
    setUploadingAttachments(true);
    try {
      const file = await normaliseFile(rawFile);
      const fd = new FormData();
      fd.append('files', file, file.name);
      const res = await apiFetch('/api/grading/me/attachments?kind=photo', {
        method: 'POST',
        body: fd,
      });
      if (!res.ok) {
        let msg = 'Photo upload failed';
        try { const data = await res.json(); if (data && data.error) msg = data.error; } catch { /* ignore */ }
        throw new Error(msg);
      }
      const data = await res.json();
      const freshPhoto = (data.attachments || [])[0];
      if (!freshPhoto) return;
      // Optimistic update so the photo preview appears immediately.
      setAttachments((prev) => [
        ...prev.filter((a) => a.kind !== 'photo'),
        freshPhoto,
      ]);
      // Then refetch the canonical bundle (same defensive pattern as
      // uploadAttachments) so the download URL matches GET /me exactly.
      try {
        const refetch = await apiGet('/api/grading/me');
        if (refetch.ok) {
          const body = await refetch.json();
          if (Array.isArray(body.attachments)) {
            setAttachments(body.attachments);
          }
        }
      } catch { /* keep optimistic state */ }
    } catch (err) {
      setAttachmentError(err.message || 'Photo upload failed');
    } finally {
      setUploadingAttachments(false);
    }
  }, []);

  // ── Cancel (revert to pristine) ─────────────────────────────────
  const handleCancel = useCallback(() => {
    const snap = rawSnapshot.current;
    if (!snap) return;
    setProfile(snap.profile || {});
    setEntries(Array.isArray(snap.entries) ? snap.entries : []);
    setStagedAttachments([]); // drop any queued uploads
    setAttachmentError('');
    setSaveError('');
    setSavedFlash(false);
  }, []);

  // ── Save (PUT) ──────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    if (savingRef.current) return;
    setSaveError('');

    // Light validation: only block on an obviously-malformed email.
    if (profile && profile.email && !EMAIL_RE.test(String(profile.email).trim())) {
      setSaveError('Please enter a valid email address before saving.');
      return;
    }
    // Block save if any reference email is present but malformed.
    if (entries.some((e) => isInvalidEmail(e && e.reference_email))) {
      setSaveError('Please enter a valid reference email address before saving.');
      return;
    }

    // 🚨 THE EXPERIENCE NUMBERS ARE REFUSED, NEVER COERCED (input audit #5, 2026-10-28). This
    // used to send `Math.max(0, Number(x) || 0)`: unreadable text (the NumberInput marker, or
    // `''` from a bare box) became a plausible stored 0, and a negative silently became 0.
    const numbersError = gradingNumbersError(profile, entries, rawSnapshot.current);
    if (numbersError) {
      setSaveError(numbersError);
      return;
    }

    // Strip the synthetic client key (_key) so it never reaches the server payload. A blank
    // weeks / odd days box sends 0, as it always has (both columns DEFAULT 0); a LOCKED entry's
    // numbers go as held (the server skips its write).
    const cleanEntries = entries.map((e) => {
      const { _key, ...rest } = e;
      return {
        ...rest,
        weeks_worked: toPayloadNumber(e.weeks_worked, 0) ?? e.weeks_worked,
        odd_days: toPayloadNumber(e.odd_days, 0) ?? e.odd_days,
        total_days: entryTotalDays(e), // locked = 5×weeks + odd days
      };
    });

    // A BLANK pyro box sends null — "not stated" (the column has no default) — never 0.
    // Strip the synthetic _key from supervisor signatures so it never reaches the server.
    const srcProfile = profile || {};
    const cleanSignatures = Array.isArray(srcProfile.supervisor_signatures)
      ? srcProfile.supervisor_signatures.map(({ _key, ...rest }) => rest)
      : srcProfile.supervisor_signatures;
    const cleanProfile = {
      ...srcProfile,
      supervisor_signatures: cleanSignatures,
      pyro_years: toPayloadNumber(srcProfile.pyro_years, null),
      pyro_months: toPayloadNumber(srcProfile.pyro_months, null),
    };

    savingRef.current = true;
    setSaving(true);
    try {
      const res = await apiPut('/api/grading/me', { profile: cleanProfile, entries: cleanEntries });
      if (!res.ok) {
        let msg = 'Failed to save grading profile';
        try {
          const data = await res.json();
          if (data && data.error) msg = data.error;
        } catch {
          // ignore parse error
        }
        throw new Error(msg);
      }
      const data = await res.json();
      const prof = data.profile || {};
      const ents = (Array.isArray(data.entries) ? data.entries : []).map((e) => ({
        ...e,
        _key: e._key || makeClientKey(),
        total_days: entryTotalDays(e), // lock to 5×weeks + odd days (fixes legacy rows)
      }));

      // Persist any staged attachment uploads before declaring save complete.
      // Done after the profile/entries save so a validation error there blocks
      // both. Each batch goes through the same /attachments endpoint.
      let serverAttachments = null;
      if (stagedAttachments.length > 0) {
        const fd = new FormData();
        for (const s of stagedAttachments) {
          // Apply the user's renamed label by overriding the File name on upload.
          // Server stores file.originalname → so we wrap the File with the new name.
          const namedFile = s.label && s.label !== s.file.name
            ? new File([s.file], s.label, { type: s.file.type })
            : s.file;
          fd.append('files', namedFile);
        }
        const upRes = await apiFetch('/api/grading/me/attachments', { method: 'POST', body: fd });
        if (!upRes.ok) {
          let msg = 'Profile saved, but attachment upload failed';
          try { const d = await upRes.json(); if (d && d.error) msg = d.error; } catch { /* ignore */ }
          throw new Error(msg);
        }
        // Refetch the canonical bundle so attachments[] reflects what's on
        // the server (with proper IDs + download_urls).
        try {
          const refetch = await apiGet('/api/grading/me');
          if (refetch.ok) {
            const body = await refetch.json();
            if (Array.isArray(body.attachments)) serverAttachments = body.attachments;
          }
        } catch { /* fall through — leave attachments as-is on refetch failure */ }
      }

      setProfile(prof);
      setEntries(ents);
      if (serverAttachments) setAttachments(serverAttachments);
      setStagedAttachments([]); // queue is cleared regardless of refetch success
      setPristine(canonicalBundle(prof, ents));
      rawSnapshot.current = { profile: prof, entries: ents };
      setSavedFlash(true);
    } catch (err) {
      setSaveError(err.message || 'Failed to save grading profile');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  }, [profile, entries, stagedAttachments]);

  // ── Early returns (AFTER all hooks) ─────────────────────────────
  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <LoadingSpinner message="Loading your grading record…" />
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <ErrorMessage error={loadError} onRetry={load} title="Could not load grading record" />
      </div>
    );
  }

  const prof = profile || {};
  const quals = prof.qualifications || {};
  const publishPerms = prof.publish_permissions || {};
  const isPyro = prof.discipline === 'pyrotechnic';

  return (
    <div className="max-w-4xl lg:max-w-6xl xl:max-w-7xl mx-auto p-2 md:p-4 lg:p-6 pb-24 space-y-6">
      {leaveGuard.dialog}
      {/* Page header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Grading</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Track your JIGS SFX grading progression
          </p>
        </div>
        {/* Header action slot — official-form PDF export. */}
        <div data-testid="grading-header-actions">
          <GradingExportButton profile={profile} entries={entries} progress={progress} attachments={attachments} />
        </div>
      </div>

      {/* Progression panel — headline visual, always visible */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4">
        <GradeProgressionBar progress={progress} />
      </div>

      {/* 1. Applicant Details */}
      <GradingSection id="grading-applicant" title="Applicant Details">
        {/* Photo column is fixed at 200px so the two data columns expand to
            fill the remaining width. On mobile/sm the photo stacks below. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_1fr_200px] gap-3">
          {/* Left column: name + address only */}
          <div className="space-y-3">
            <div>
              <label htmlFor="g-full-name" className={labelClass}>Full name</label>
              <input
                id="g-full-name"
                type="text"
                value={prof.full_name || ''}
                onChange={(e) => setRememberedField('full_name', e.target.value, LS_KEYS.fullName)}
                className={inputClass}
                data-testid="grading-full-name"
              />
            </div>
            <div>
              <label htmlFor="g-address" className={labelClass}>Address</label>
              <textarea
                id="g-address"
                rows={4}
                value={prof.address || ''}
                onChange={(e) => setProfileField('address', e.target.value)}
                className={textareaClass}
                data-testid="grading-address"
              />
            </div>
            <div>
              <label htmlFor="g-postcode" className={labelClass}>Post code</label>
              <input
                id="g-postcode"
                type="text"
                value={prof.post_code || ''}
                onChange={(e) => setProfileField('post_code', e.target.value)}
                className={inputClass}
                data-testid="grading-postcode"
              />
            </div>
          </div>
          {/* Right column: everything else */}
          <div className="space-y-3">
            <div>
              <label htmlFor="g-dob" className={labelClass}>Date of birth</label>
              <input
                id="g-dob"
                type="date"
                value={prof.date_of_birth || ''}
                onChange={(e) => setProfileField('date_of_birth', e.target.value)}
                className={inputClass}
                data-testid="grading-dob"
              />
            </div>
            <div>
              <label htmlFor="g-email" className={labelClass}>Email</label>
              <input
                id="g-email"
                type="email"
                value={prof.email || ''}
                onChange={(e) => setRememberedField('email', e.target.value, LS_KEYS.email)}
                className={inputClass}
                data-testid="grading-email"
              />
            </div>
            <div>
              <label htmlFor="g-phone" className={labelClass}>Phone</label>
              <input
                id="g-phone"
                type="tel"
                value={prof.phone || ''}
                onChange={(e) => setRememberedField('phone', e.target.value, LS_KEYS.phone)}
                className={inputClass}
                data-testid="grading-phone"
              />
            </div>
          </div>
          {/* Third column: passport-style photo */}
          <div>
            <PhotoUpload
              photo={photo}
              onUpload={uploadPhoto}
              onRemove={() => photo && deleteAttachment(photo.id)}
              uploading={uploadingAttachments}
            />
          </div>
        </div>
      </GradingSection>

      {/* 2. Grade & Discipline */}
      <GradingSection id="grading-grade" title="Grade & Discipline">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label htmlFor="g-discipline" className={labelClass}>Discipline</label>
            <select
              id="g-discipline"
              value="physical"
              disabled
              aria-readonly="true"
              className={`${inputClass} bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 cursor-not-allowed`}
              data-testid="grading-discipline"
            >
              <option value="physical">{DISCIPLINE_LABELS.physical || 'Physical SFX'}</option>
            </select>
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              Pyrotechnic and Snow grading not yet available.
            </p>
          </div>
          <div>
            <label htmlFor="g-current-grade" className={labelClass}>Current grade</label>
            <select
              id="g-current-grade"
              value={prof.current_grade || ''}
              onChange={(e) => setProfileField('current_grade', e.target.value)}
              className={inputClass}
              data-testid="grading-current-grade"
            >
              <option value="">None</option>
              {GRADE_ORDER.map((g) => (
                <option key={g} value={g}>{GRADE_LABELS[g]}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="g-grade-applied" className={labelClass}>Grade applied for</label>
            <select
              id="g-grade-applied"
              value={prof.grade_applied_for || ''}
              onChange={(e) => setProfileField('grade_applied_for', e.target.value)}
              className={inputClass}
              data-testid="grading-grade-applied"
            >
              <option value="">None</option>
              {GRADE_ORDER.map((g) => (
                <option key={g} value={g}>{GRADE_LABELS[g]}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="g-subsidiary" className={labelClass}>Subsidiary title</label>
            <input
              id="g-subsidiary"
              type="text"
              value={prof.subsidiary_title || ''}
              onChange={(e) => setProfileField('subsidiary_title', e.target.value)}
              className={inputClass}
              data-testid="grading-subsidiary-title"
            />
          </div>
          <div>
            <label htmlFor="g-entered" className={labelClass}>Date entered industry</label>
            <input
              id="g-entered"
              type="date"
              value={prof.date_entered_industry || ''}
              onChange={(e) => setProfileField('date_entered_industry', e.target.value)}
              className={inputClass}
              data-testid="grading-date-entered"
            />
          </div>
          <div>
            <label htmlFor="g-last-grading" className={labelClass}>Date of last grading</label>
            <input
              id="g-last-grading"
              type="date"
              value={prof.date_last_grading || ''}
              onChange={(e) => setProfileField('date_last_grading', e.target.value)}
              className={inputClass}
              data-testid="grading-date-last"
            />
          </div>
          {isPyro && (
            <>
              <div>
                <label htmlFor="g-pyro-years" className={labelClass}>Pyrotechnic years</label>
                {/* ui/NumberInput (input audit #5): unreadable text is held as the marker and
                    refused at Save by name — never clamped to '' or 0 here. */}
                <NumberInput
                  id="g-pyro-years"
                  integer
                  value={prof.pyro_years == null ? '' : prof.pyro_years}
                  onChange={(v) => setProfileField('pyro_years', v)}
                  className={inputClass}
                  testId="grading-pyro-years"
                />
              </div>
              <div>
                <label htmlFor="g-pyro-months" className={labelClass}>Pyrotechnic months</label>
                <NumberInput
                  id="g-pyro-months"
                  integer
                  value={prof.pyro_months == null ? '' : prof.pyro_months}
                  onChange={(v) => setProfileField('pyro_months', v)}
                  className={inputClass}
                  testId="grading-pyro-months"
                />
              </div>
            </>
          )}
        </div>
      </GradingSection>

      {/* 3. Qualifications */}
      <GradingSection id="grading-quals" title="Qualifications">
        <div className="space-y-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Explosives qualifications feed grade eligibility for the Pyrotechnic discipline.
          </p>
          <div className="space-y-2">
            {QUALIFICATIONS.map((q) => {
              const yes = !!quals[q.id];
              return (
                <div
                  key={q.id}
                  className="flex items-center justify-between gap-3 p-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30"
                >
                  <span className="text-sm text-gray-700 dark:text-gray-300">{q.name}</span>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setQualification(q.id, true)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                        yes
                          ? 'bg-green-600 text-white dark:bg-green-500/70'
                          : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                      data-testid={`grading-qual-${q.id}-yes`}
                      aria-pressed={yes}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => setQualification(q.id, false)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                        !yes
                          ? 'bg-gray-600 text-white dark:bg-gray-500/70'
                          : 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300'
                      }`}
                      data-testid={`grading-qual-${q.id}-no`}
                      aria-pressed={!yes}
                    >
                      No
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          <div>
            <label htmlFor="g-other-quals" className={labelClass}>Other qualifications</label>
            <textarea
              id="g-other-quals"
              rows={3}
              value={prof.other_qualifications || ''}
              onChange={(e) => setProfileField('other_qualifications', e.target.value)}
              className={textareaClass}
              data-testid="grading-other-quals"
            />
          </div>
        </div>
      </GradingSection>

      {/* 4. Experience (repeating line items) */}
      <GradingSection
        id="grading-experience"
        title={`Experience (${entries.length})`}
      >
        <div className="space-y-4">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            List each production you have worked on. Weeks are 5-day weeks; total days can
            be entered independently.
          </p>

          {entries.length === 0 && (
            <p className="text-sm italic text-gray-400 dark:text-gray-500" data-testid="grading-no-entries">
              No productions added yet.
            </p>
          )}

          {entries.map((entry, index) => {
            const rowKey = entryKey(entry);
            const collapsed = collapsedEntries.has(rowKey);
            const locked = isLocked(entry);
            const status = entry.approval_status || 'none';
            const entryApprovalError = entry.id != null ? approvalError[entry.id] : '';
            // Tiny status pill shown in both the collapsed header and reference cell.
            const statusPill =
              status === 'pending'
                ? { cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300', label: '⏳ Pending' }
                : status === 'approved'
                ? { cls: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300', label: '✓ Approved' }
                : status === 'rejected'
                ? { cls: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300', label: '✗ Declined' }
                : null;
            // Request Approval gating: saved row, nothing dirty anywhere, valid
            // non-empty reference email.
            const refEmail = (entry.reference_email || '').trim();
            const emailOk = refEmail !== '' && !isInvalidEmail(entry.reference_email);
            const canRequest = entry.id != null && !isDirty && emailOk;
            const requestHint = !emailOk
              ? 'Add a valid reference email'
              : entry.id == null || isDirty
              ? 'Save your changes first'
              : '';
            return (
              <div
                key={entry.id ?? entry._key}
                className={`border border-gray-200 dark:border-gray-700 rounded-lg ${collapsed ? 'p-3' : 'p-4'} bg-gray-50 dark:bg-gray-700/30`}
                data-testid={`grading-entry-${index}`}
              >
                <div className={`flex items-center justify-between ${collapsed ? '' : 'mb-3'}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <button
                      type="button"
                      onClick={() => toggleEntryCollapse(rowKey)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors p-0.5"
                      title={collapsed ? 'Expand' : 'Collapse'}
                      aria-label={collapsed ? `Expand production ${index + 1}` : `Collapse production ${index + 1}`}
                      aria-expanded={!collapsed}
                      data-testid={`grading-entry-toggle-${index}`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`h-4 w-4 transition-transform ${collapsed ? '-rotate-90' : ''}`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <span className="text-sm font-semibold text-gray-500 dark:text-gray-400 flex-shrink-0">
                      #{index + 1}
                    </span>
                    {collapsed && (
                      <span className="text-sm text-gray-700 dark:text-gray-300 truncate">
                        {entry.production_name || <span className="italic text-gray-400 dark:text-gray-500">Untitled</span>}
                        {entry.credited_grade && (
                          <span className="text-gray-500 dark:text-gray-400"> — {GRADE_LABELS[entry.credited_grade] || entry.credited_grade}</span>
                        )}
                        <span className="text-gray-500 dark:text-gray-400"> — {entryTotalDays(entry)} d</span>
                      </span>
                    )}
                    {collapsed && statusPill && (
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${statusPill.cls}`}
                        data-testid={`grading-entry-status-pill-${index}`}
                      >
                        {statusPill.label}
                      </span>
                    )}
                  </div>
                  {!collapsed && !locked && (
                    <button
                      type="button"
                      onClick={() => removeEntry(index)}
                      className="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium flex-shrink-0"
                      data-testid={`grading-entry-remove-${index}`}
                    >
                      Remove
                    </button>
                  )}
                </div>

                {!collapsed && status === 'rejected' && (
                  <div
                    className="mb-3 rounded-lg px-3 py-2 text-sm bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300"
                    data-testid={`grading-entry-rejected-banner-${index}`}
                  >
                    ✗ Declined{entry.approval_decided_by ? ` by ${entry.approval_decided_by}` : ''}
                    {entry.approval_reject_reason ? `: ${entry.approval_reject_reason}` : ''}
                  </div>
                )}

                {!collapsed && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    <div className="sm:col-span-2 lg:col-span-1">
                      <label className={FIELD_LABEL_CLASS}>Production</label>
                      <input
                        type="text"
                        value={entry.production_name || ''}
                        onChange={(e) => updateEntry(index, 'production_name', e.target.value)}
                        disabled={locked}
                        className={`${inputClass}${locked ? LOCKED_INPUT : ''}`}
                        data-testid={`grading-entry-production-${index}`}
                      />
                    </div>
                    <div>
                      <label className={FIELD_LABEL_CLASS}>Credited grade</label>
                      <select
                        value={entry.credited_grade || ''}
                        onChange={(e) => updateEntry(index, 'credited_grade', e.target.value)}
                        disabled={locked}
                        className={`${inputClass}${locked ? LOCKED_INPUT : ''}`}
                        data-testid={`grading-entry-grade-${index}`}
                      >
                        <option value="">--</option>
                        {GRADE_ORDER.map((g) => (
                          <option key={g} value={g}>{GRADE_LABELS[g]}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className={FIELD_LABEL_CLASS}>Start date</label>
                      <input
                        type="date"
                        value={entry.start_date || ''}
                        onChange={(e) => updateEntry(index, 'start_date', e.target.value)}
                        disabled={locked}
                        className={`${inputClass}${locked ? LOCKED_INPUT : ''}`}
                        data-testid={`grading-entry-start-${index}`}
                      />
                    </div>
                    <div>
                      <label className={FIELD_LABEL_CLASS}>End date</label>
                      <input
                        type="date"
                        value={entry.end_date || ''}
                        onChange={(e) => updateEntry(index, 'end_date', e.target.value)}
                        disabled={locked}
                        className={`${inputClass}${locked ? LOCKED_INPUT : ''}`}
                        data-testid={`grading-entry-end-${index}`}
                      />
                    </div>
                    <div>
                      <label className={FIELD_LABEL_CLASS}>Weeks worked (5-day)</label>
                      {/* Weeks may be fractional (REAL column; ≤2 dp, server-checked). */}
                      <NumberInput
                        step="0.01"
                        value={entry.weeks_worked == null ? '' : entry.weeks_worked}
                        onChange={(v) => updateEntry(index, 'weeks_worked', v)}
                        disabled={locked}
                        className={`${inputClass}${locked ? LOCKED_INPUT : ''}`}
                        testId={`grading-entry-weeks-${index}`}
                      />
                    </div>
                    <div>
                      <label className={FIELD_LABEL_CLASS}>Odd days</label>
                      <NumberInput
                        integer
                        value={entry.odd_days == null ? '' : entry.odd_days}
                        onChange={(v) => updateEntry(index, 'odd_days', v)}
                        disabled={locked}
                        className={`${inputClass}${locked ? LOCKED_INPUT : ''}`}
                        testId={`grading-entry-odd-${index}`}
                      />
                    </div>
                    <div>
                      <label className={FIELD_LABEL_CLASS}>
                        Total days <span className="text-gray-400 dark:text-gray-500">(auto: 5×weeks + odd)</span>
                      </label>
                      <input
                        type="number"
                        readOnly
                        tabIndex={-1}
                        aria-label="Total days (auto-calculated)"
                        value={entryTotalDays(entry)}
                        className={`${inputClass} bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 cursor-not-allowed`}
                        data-testid={`grading-entry-total-${index}`}
                      />
                    </div>
                    <div className="sm:col-span-2 lg:col-span-3">
                      <label className={FIELD_LABEL_CLASS}>Example of effects &amp; materials / techniques used</label>
                      <textarea
                        rows={10}
                        value={entry.effects_techniques || ''}
                        onChange={(e) => updateEntry(index, 'effects_techniques', e.target.value)}
                        disabled={locked}
                        className={`${textareaClass}${locked ? LOCKED_INPUT : ''}`}
                        data-testid={`grading-entry-techniques-${index}`}
                      />
                    </div>
                    <div className="sm:col-span-2 lg:col-span-3">
                      <label className={FIELD_LABEL_CLASS}>Notes (optional)</label>
                      <textarea
                        rows={2}
                        value={entry.notes || ''}
                        onChange={(e) => updateEntry(index, 'notes', e.target.value)}
                        disabled={locked}
                        className={`${textareaClass}${locked ? LOCKED_INPUT : ''}`}
                        data-testid={`grading-entry-notes-${index}`}
                      />
                    </div>

                    {/* Reference block — one reference per production. The
                        production's supervisor name doubles as the reference
                        name; the title comes from the standard role list. */}
                    <div className="sm:col-span-2 lg:col-span-3 mt-1 pt-3 border-t border-gray-200 dark:border-gray-600">
                      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                        Reference
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                        <div>
                          <label className={FIELD_LABEL_CLASS}>Reference name</label>
                          <input
                            type="text"
                            value={entry.supervisor_name || ''}
                            onChange={(e) => updateEntry(index, 'supervisor_name', e.target.value)}
                            disabled={locked}
                            className={`${inputClass}${locked ? LOCKED_INPUT : ''}`}
                            data-testid={`grading-entry-ref-name-${index}`}
                          />
                        </div>
                        <div>
                          <label className={FIELD_LABEL_CLASS}>Title</label>
                          <select
                            value={entry.reference_title || ''}
                            onChange={(e) => updateEntry(index, 'reference_title', e.target.value)}
                            disabled={locked}
                            className={`${inputClass}${locked ? LOCKED_INPUT : ''}`}
                            data-testid={`grading-entry-ref-title-${index}`}
                          >
                            <option value="">--</option>
                            {REFERENCE_TITLES.map((t) => (
                              <option key={t} value={t}>{t}</option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className={FIELD_LABEL_CLASS}>Reference email</label>
                          <input
                            type="email"
                            value={entry.reference_email || ''}
                            onChange={(e) => updateEntry(index, 'reference_email', e.target.value)}
                            aria-invalid={isInvalidEmail(entry.reference_email)}
                            disabled={locked}
                            className={`${inputClass} ${isInvalidEmail(entry.reference_email) ? 'border-red-500 dark:border-red-500' : ''}${locked ? LOCKED_INPUT : ''}`}
                            data-testid={`grading-entry-ref-email-${index}`}
                          />
                          {isInvalidEmail(entry.reference_email) && (
                            <p className="mt-1 text-xs text-red-500 dark:text-red-400">Enter a valid email address.</p>
                          )}
                        </div>
                        <div className="flex flex-col justify-end gap-1.5">
                          {(status === 'none' || status === 'rejected') && (
                            <>
                              <button
                                type="button"
                                onClick={() => setConfirmRequestIndex(index)}
                                disabled={!canRequest}
                                title={requestHint || undefined}
                                className={`${BTN.primary} w-full`}
                                data-testid={`grading-entry-ref-request-${index}`}
                              >
                                Request Approval
                              </button>
                              {requestHint && (
                                <p className="text-xs text-gray-400 dark:text-gray-500">{requestHint}</p>
                              )}
                            </>
                          )}

                          {status === 'pending' && (
                            <>
                              <span
                                className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                                data-testid={`grading-entry-ref-pending-${index}`}
                              >
                                ⏳ Pending approval
                              </span>
                              {entry.reference_email && (
                                <p className="text-xs text-gray-400 dark:text-gray-500 truncate">
                                  Sent to {entry.reference_email}
                                </p>
                              )}
                              <button
                                type="button"
                                onClick={() => withdraw(index)}
                                className={`${BTN.secondary} w-full`}
                                data-testid={`grading-entry-ref-unlock-${index}`}
                              >
                                Unlock
                              </button>
                            </>
                          )}

                          {status === 'approved' && (
                            <>
                              <span
                                className="inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"
                                data-testid={`grading-entry-ref-approved-${index}`}
                              >
                                ✓ Approved{entry.approval_decided_by ? ` by ${entry.approval_decided_by}` : ''}
                              </span>
                              <button
                                type="button"
                                onClick={() => withdraw(index)}
                                className={`${BTN.secondary} w-full`}
                                data-testid={`grading-entry-ref-unlock-${index}`}
                              >
                                Unlock
                              </button>
                              <p className="text-xs text-gray-400 dark:text-gray-500">
                                Unlocking voids this approval.
                              </p>
                            </>
                          )}

                          {entryApprovalError && (
                            <p
                              className="text-xs text-red-600 dark:text-red-400"
                              data-testid={`grading-entry-ref-error-${index}`}
                            >
                              {entryApprovalError}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <button
            type="button"
            onClick={addEntry}
            className={BTN.secondary}
            data-testid="grading-add-entry"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
            Add production
          </button>
        </div>
      </GradingSection>

      {/* 5. Other Experience */}
      <GradingSection id="grading-other-experience" title="Other Experience">
        <div>
          <textarea
            rows={4}
            value={prof.other_experience || ''}
            onChange={(e) => setProfileField('other_experience', e.target.value)}
            className={textareaClass}
            placeholder="Any other relevant experience…"
            data-testid="grading-other-experience"
          />
        </div>
      </GradingSection>

      {/* 6. Supporting documents — qualifications, RAs, passport photo etc.
              Appended to the application PDF on export. */}
      <GradingSection id="grading-attachments" title="Supporting documents">
        <div className="space-y-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Add scans of qualifications, risk assessments, or anything else
            that supports your application. These will be appended to your
            application PDF — one image per page, PDFs in full. (Your
            passport-style photo lives in <em>Applicant Details</em> above.)
          </p>

          {attachmentError && (
            <div className="rounded-lg px-3 py-2 text-sm bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300" data-testid="grading-attachment-error">
              {attachmentError}
            </div>
          )}

          <FileDropArea
            accept="image/jpeg,image/png,image/heic,image/heif,application/pdf,.heic,.heif"
            onFiles={uploadAttachments}
            disabled={(documentAttachments.length + stagedAttachments.length) >= MAX_ATTACHMENTS}
          />

          {uploadingAttachments && (
            <p className="text-xs text-gray-500 dark:text-gray-400" data-testid="grading-attachments-uploading">
              Processing…
            </p>
          )}

          {(documentAttachments.length > 0 || stagedAttachments.length > 0) && (
            <ul className="space-y-2" data-testid="grading-attachments-list">
              {documentAttachments.map((att) => (
                <AttachmentRow
                  key={att.id}
                  attachment={att}
                  onLabelChange={(label) => updateAttachmentLabel(att.id, label)}
                  onDelete={() => deleteAttachment(att.id)}
                />
              ))}
              {stagedAttachments.map((s) => (
                <StagedAttachmentRow
                  key={s._key}
                  staged={s}
                  onLabelChange={(label) => updateStagedLabel(s._key, label)}
                  onRemove={() => removeStagedAttachment(s._key)}
                />
              ))}
            </ul>
          )}

          {stagedAttachments.length > 0 && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              {stagedAttachments.length} file(s) queued — click <strong>Save changes</strong> to upload.
              Files won&apos;t be downloadable until then.
            </p>
          )}

          {(documentAttachments.length + stagedAttachments.length) > 0 && (
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {documentAttachments.length + stagedAttachments.length} of {MAX_ATTACHMENTS} attachments used.
            </p>
          )}
        </div>
      </GradingSection>

      {/* 7. Publish Permissions (GDPR) */}
      <GradingSection id="grading-publish" title="Publish Permissions">
        <div className="space-y-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Choose which contact details you consent to publish in the graded crew directory.
            You can change these at any time.
          </p>
          <div className="flex flex-wrap gap-3">
            {PUBLISH_FIELDS.map((f) => (
              <label
                key={f.id}
                className="flex items-center gap-2 cursor-pointer px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30"
              >
                <input
                  type="checkbox"
                  checked={!!publishPerms[f.id]}
                  onChange={(e) => setPublishPermission(f.id, e.target.checked)}
                  className="h-4 w-4 text-blue-600 border-gray-300 dark:border-gray-600 rounded focus:ring-blue-500"
                  data-testid={`grading-publish-${f.id}`}
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{f.label}</span>
              </label>
            ))}
          </div>
        </div>
      </GradingSection>

      {/* 8. Outstanding requirements — spells out WHY each grade is "Not yet".
          The bar above tracks weeks only; a grade needs ALL of its requirements
          (years, weeks, supervisor references, qualifications, and holding
          the grade below it). */}
      <GradingSection id="grading-requirements" title="Grade requirements — what's still missing">
        <div className="space-y-3" data-testid="grading-requirements-list">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Each grade is reached only when <strong>every</strong> requirement is met. The
            progression bar tracks weeks alone — this list shows everything still outstanding
            for each grade in {DISCIPLINE_LABELS[progress.discipline] || 'this discipline'}.
          </p>
          {(progress.eligibility || []).map((item) => {
            const requirements = Array.isArray(item.requirements) ? item.requirements : [];
            const status = item.status in STATUS_PILL ? item.status : 'fail';
            return (
              <div
                key={item.grade}
                className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-gray-50 dark:bg-gray-700/30"
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <span className="font-medium text-gray-800 dark:text-gray-200">
                    {GRADE_LABELS[item.grade] || item.grade}
                  </span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_PILL[status]}`}>
                    {STATUS_LABEL[status]}
                  </span>
                </div>
                <ul className="space-y-1 text-sm">
                  {requirements.map((r) => {
                    // Icon states:
                    //   met true + met_via_prior true  → amber half tick (credited via prior grading, proof needed)
                    //   met true                       → green tick (fully met by logged work)
                    //   met false                      → red cross
                    //   met null                       → grey info dot (manual verify)
                    const isPrior = r.met === true && r.met_via_prior === true;
                    const icon = isPrior
                      ? '◐'
                      : r.met === true ? '✓' : r.met === false ? '✗' : 'ℹ';
                    const iconClass = isPrior
                      ? 'text-amber-500 dark:text-amber-400'
                      : r.met === true
                      ? 'text-green-600 dark:text-green-400'
                      : r.met === false
                      ? 'text-red-500 dark:text-red-400'
                      : 'text-gray-400 dark:text-gray-500';
                    const textClass =
                      r.met === true && !isPrior
                        ? 'text-gray-500 dark:text-gray-400'
                        : 'text-gray-700 dark:text-gray-200';
                    return (
                      <li key={r.key} className="flex items-start gap-2">
                        <span className={`mt-0.5 font-semibold ${iconClass}`} aria-hidden="true">{icon}</span>
                        <div className="flex-1 min-w-0">
                          <span className={textClass}>
                            {r.label}
                            {r.detail ? (
                              <span className="text-gray-400 dark:text-gray-500"> — {r.detail}</span>
                            ) : null}
                          </span>
                          {r.note ? (
                            <p className="text-xs text-amber-700 dark:text-amber-400 mt-0.5" data-testid={`grading-req-note-${r.key}`}>
                              {r.note}
                            </p>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </div>
      </GradingSection>

      {/* Sticky save-bar — appears only when dirty (mirrors RiskAssessmentDetail) */}
      {(isDirty || savedFlash || saveError) && (
        <div
          className="fixed bottom-16 lg:bottom-0 left-0 right-0 lg:left-[var(--sidebar-width)] bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 px-4 py-2.5 z-[55]"
          data-testid="grading-save-bar"
        >
          <div className="max-w-full mx-auto flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs">
              {saveError ? (
                <span className="text-red-600 dark:text-red-400 font-medium">{saveError}</span>
              ) : isDirty ? (
                <span className="text-amber-600 dark:text-amber-400 font-medium">● Unsaved changes</span>
              ) : savedFlash ? (
                <span className="text-green-600 dark:text-green-400 font-medium">✓ Saved</span>
              ) : (
                <span className="text-gray-500 dark:text-gray-400">All saved</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isDirty && (
                <button
                  type="button"
                  onClick={handleCancel}
                  disabled={saving}
                  className={BTN.secondary}
                  data-testid="grading-cancel"
                >
                  Cancel
                </button>
              )}
              <button
                type="button"
                onClick={handleSave}
                disabled={!isDirty || saving}
                className={BTN.confirm}
                data-testid="grading-save"
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Send-for-approval confirmation — guards against accidental sends
          of half-finished paperwork. Shows the referee's name + email so
          the user can double-check who's about to receive the request. */}
      {confirmRequestIndex !== null && (() => {
        const entry = entries[confirmRequestIndex];
        if (!entry) return null;
        const refName = (entry.supervisor_name && entry.supervisor_name.trim()) || 'the named reference';
        const refEmail = (entry.reference_email && entry.reference_email.trim()) || '';
        const sendIndex = confirmRequestIndex;
        return (
          <div
            className={`fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 ${MODAL_OVERLAY}`}
            data-testid="grading-request-confirm-backdrop"
            onClick={() => setConfirmRequestIndex(null)}
          >
            <div
              className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-5 ${MODAL_PANEL_MOBILE_CAP}`}
              onClick={(e) => e.stopPropagation()}
              data-testid="grading-request-confirm-modal"
            >
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Send this experience for approval?
              </h3>
              <p className="mt-3 text-sm text-gray-700 dark:text-gray-300">
                This will email the experience section to{' '}
                <strong className="text-gray-900 dark:text-white">{refName}</strong>
                {refEmail && (
                  <> at <span className="font-mono text-xs text-gray-700 dark:text-gray-300">{refEmail}</span></>
                )} for confirmation.
              </p>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                The section will be locked until they respond or you withdraw the request. Double-check the
                details first before sending.
              </p>
              <div className="mt-5 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmRequestIndex(null)}
                  className={BTN.secondary}
                  data-testid="grading-request-confirm-cancel"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setConfirmRequestIndex(null);
                    requestApproval(sendIndex);
                  }}
                  className={BTN.primary}
                  data-testid="grading-request-confirm-send"
                >
                  Send for approval
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default Grading;
